/**
 * PracticeContext — central practice state for the multi-tenant frontend (Session 10).
 *
 * Calls GET /api/users/me/practices once on auth. Exposes:
 *   practiceId         current practice's UUID
 *   practiceRole       'owner' | 'member' | 'task_manager' | 'super_admin'
 *   isUserSuperAdmin   true if the JWT carries the super_admin Keycloak role
 *   isUserAdmin        true if the JWT carries the (narrower) admin Keycloak role
 *   ownerView          "Inhaber-Ansicht" is on: the three role values above read as
 *                      the practice owner's and availablePractices holds only the
 *                      current practice. Display only, the token is unchanged.
 *   canUseOwnerView    the real super_admin check; gates the switch itself
 *   toggleOwnerView()  flip the owner view (persists to localStorage)
 *   currentPractice    {practice_id, practice_name, role} for the selected practice
 *   availablePractices array of all practices the user can access
 *   switchPractice(id) switch the active practice (persists to localStorage)
 *   allPracticesMode   "Alle Praxen" toggle state, SHARED by every page that can pool
 *   setAllPracticesMode(bool)
 *   refreshPractices() re-fetch /me/practices (used after create/delete in AdminPracticesPage)
 *   loading            true while the initial fetch is in flight
 *   logoUrl + refreshLogo + clearLogo  (existing API for PracticeLogo — unchanged)
 *
 * Persistence: the last selected practiceId is stored in localStorage('lastPracticeId')
 * so the choice survives page reloads. Restored on next load if still in the user's
 * available list; otherwise falls back to the first available practice.
 */
import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import axios from '../utils/axiosInstance';
import { useAuth, isAdmin, isSuperAdmin } from '../auth/AuthContext';
import { isoDay } from '../utils/dateRange';
import { fetchPracticeDashboard } from '../utils/dashboardData';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const LAST_PRACTICE_KEY = 'lastPracticeId';
const OWNER_VIEW_KEY = 'deskbot.ownerView';

const PracticeContext = createContext(null);

export const PracticeProvider = ({ children }) => {
  const { keycloak } = useAuth();
  const queryClient = useQueryClient();
  const [availablePractices, setAvailablePractices] = useState([]);
  const [currentPractice, setCurrentPractice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoUrl, setLogoUrl] = useState(null);
  // "Alle Praxen": ONE toggle state, shared by every page that can pool practices
  // (Dashboard, Tasks). It lives HERE, above <Routes> in App.js, so walking from
  // one such page to the other carries the choice — the control is one piece of
  // logic rendered in several places, not several lookalike buttons each with
  // their own useState. What the mode RESOLVES TO stays per page: usePracticeScope
  // asks the scope endpoint separately, because a mixed-role caller legitimately
  // reaches further on Tasks than on the Dashboard (see the endpoint's `for_tasks`).
  const [allPracticesMode, setAllPracticesMode] = useState(false);

  // Owner view ("Inhaber-Ansicht", switched in Layout): a super_admin sees the app
  // as the practice owner does, e.g. while sharing the screen. Read in the initializer
  // so a reload mid-share never paints the admin view; `storage` keeps tabs in step.
  const canUseOwnerView = isSuperAdmin(keycloak);
  const [ownerViewOn, setOwnerViewOn] = useState(() => {
    try { return localStorage.getItem(OWNER_VIEW_KEY) === '1'; } catch { return false; }
  });
  const ownerView = canUseOwnerView && ownerViewOn;
  const toggleOwnerView = useCallback(() => {
    const next = !ownerViewOn;
    setOwnerViewOn(next);
    try { localStorage.setItem(OWNER_VIEW_KEY, next ? '1' : '0'); } catch { /* ignore */ }
  }, [ownerViewOn]);
  useEffect(() => {
    const onStorage = (e) => { if (e.key === OWNER_VIEW_KEY) setOwnerViewOn(e.newValue === '1'); };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // The role checks every page reads. The owner view switches the admin ones off.
  const isUserSuperAdmin = canUseOwnerView && !ownerView;
  const isUserAdmin = isAdmin(keycloak) && !ownerView;

  // Restore the previous selection from localStorage (if still in the list).
  // Otherwise fall back to the first practice. NULL is fine when the user has none.
  const pickInitial = useCallback((list) => {
    if (!list || list.length === 0) return null;
    const remembered = (() => {
      try { return localStorage.getItem(LAST_PRACTICE_KEY); } catch { return null; }
    })();
    if (remembered) {
      const match = list.find((p) => p.practice_id === remembered);
      if (match) return match;
    }
    return list[0];
  }, []);

  // Fetch the user's accessible practices.
  const refreshPractices = useCallback(async () => {
    if (!keycloak?.authenticated || !keycloak?.token) {
      setAvailablePractices([]);
      setCurrentPractice(null);
      setLoading(false);
      return;
    }
    try {
      const resp = await axios.get(`${API_BASE_URL}/users/me/practices`);
      const list = Array.isArray(resp.data) ? resp.data : [];
      setAvailablePractices(list);
      setCurrentPractice((prev) => {
        // Keep the current selection if it's still in the list; otherwise re-pick.
        if (prev && list.find((p) => p.practice_id === prev.practice_id)) return prev;
        return pickInitial(list);
      });
    } catch (err) {
      // Silently fail — pages handle the empty-list state.
      console.warn('[PracticeContext] /me/practices failed:', err?.message);
      setAvailablePractices([]);
      setCurrentPractice(null);
    } finally {
      setLoading(false);
    }
  }, [keycloak, pickInitial]);

  // Initial load once auth is ready.
  useEffect(() => {
    if (!keycloak?.authenticated) return;
    setLoading(true);
    refreshPractices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak?.authenticated]);

  // Switch the active practice + persist to localStorage so reloads remember it.
  const switchPractice = useCallback((practiceId) => {
    const next = availablePractices.find((p) => p.practice_id === practiceId);
    if (!next) return;
    setCurrentPractice(next);
    try { localStorage.setItem(LAST_PRACTICE_KEY, practiceId); } catch { /* ignore */ }
  }, [availablePractices]);

  const practiceId = currentPractice?.practice_id || null;
  // Owner view: the owner's role, and only the practice on screen to list (the
  // switcher and the member import would otherwise name every client).
  const practiceRole = (ownerView && currentPractice) ? 'owner' : (currentPractice?.role || null);
  const visiblePractices = useMemo(
    () => (ownerView ? (currentPractice ? [currentPractice] : []) : availablePractices),
    [ownerView, currentPractice, availablePractices]
  );

  // Switching practice re-asks the scope question and may get a different answer
  // (a super_admin mirrors whichever practice is on screen), so the aggregate
  // cannot stay on across a switch — it would keep pooling the previous group.
  // This rule lived in DashboardPage while that was the only aggregating page;
  // it belongs here now that the toggle is shared, so it cannot be enforced on
  // one page and forgotten on the next.
  useEffect(() => {
    setAllPracticesMode(false);
  }, [practiceId]);

  // Logo fetch — bound to current practiceId. Cache-buster (?t=) is REQUIRED:
  // a practice with no logo returns 404, which the browser caches; on switching
  // back after a later upload, the plain URL would re-serve that stale 404 and
  // the logo would look "reset". A fresh ?t= per load avoids the stale 200/404.
  useEffect(() => {
    if (!practiceId) {
      setLogoUrl(null);
      return;
    }
    // Staleness guard (Jul 2026 cross-tenant incident): a slow logo fetch from
    // a previously-selected practice must not land after a switch, or the UI
    // shows another practice's logo.
    let cancelled = false;
    const url = `${API_BASE_URL}/practices/${practiceId}/logo?t=${Date.now()}`;
    fetch(url)
      .then((res) => { if (!cancelled) setLogoUrl(res.ok ? url : null); })
      .catch(() => { if (!cancelled) setLogoUrl(null); });
    return () => { cancelled = true; };
  }, [practiceId]);

  const refreshLogo = useCallback(() => {
    if (!practiceId) return;
    const url = `${API_BASE_URL}/practices/${practiceId}/logo?t=${Date.now()}`;
    fetch(url)
      .then((res) => { setLogoUrl(res.ok ? url : null); })
      .catch(() => setLogoUrl(null));
  }, [practiceId]);

  // Prefetch (phase 2, light): when the active practice changes, warm ONLY the
  // Dashboard queries so the landing page paints instantly. The query key and
  // fetcher MUST match DashboardPage exactly — ['dashboard', practiceId,
  // timePeriod] with timePeriod = 'week' (the dashboard's default selection) —
  // so the prefetched data populates the same cache entry DashboardPage reads.
  // prefetchQuery is a no-op if a fresh (non-stale) entry already exists.
  useEffect(() => {
    if (!practiceId) return;
    // Mirror DashboardPage's calculateDateRange('week'): Monday of the current
    // week → today, formatted YYYY-MM-DD.
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - daysSinceMonday);
    const start = isoDay(startDate);
    const end = isoDay(today);

    queryClient.prefetchQuery(
      ['dashboard', practiceId, 'week'],
      // The SAME fetch DashboardPage runs, imported rather than copied: this
      // warms the page's own cache entry, so any divergence would show one set
      // of numbers on arrival and another after the page's first refetch.
      () => fetchPracticeDashboard(practiceId, start, end)
    ).catch(() => { /* prefetch is best-effort — never surface errors */ });
  }, [practiceId, queryClient]);

  const clearLogo = useCallback(() => {
    setLogoUrl(null);
  }, []);

  const value = useMemo(() => ({
    practiceId,
    practiceRole,
    isUserSuperAdmin,
    isUserAdmin,
    ownerView,
    canUseOwnerView,
    toggleOwnerView,
    currentPractice,
    availablePractices: visiblePractices,
    switchPractice,
    refreshPractices,
    loading,
    allPracticesMode,
    setAllPracticesMode,
    // Legacy logo API (preserved for PracticeLogo component).
    logoUrl,
    refreshLogo,
    clearLogo,
  }), [
    practiceId, practiceRole, isUserSuperAdmin, isUserAdmin,
    ownerView, canUseOwnerView, toggleOwnerView, currentPractice, visiblePractices,
    switchPractice, refreshPractices, loading,
    allPracticesMode, setAllPracticesMode,
    logoUrl, refreshLogo, clearLogo,
  ]);

  return (
    <PracticeContext.Provider value={value}>
      {children}
    </PracticeContext.Provider>
  );
};

export const usePractice = () => {
  const context = useContext(PracticeContext);
  if (context === null) {
    throw new Error('usePractice must be used within a PracticeProvider');
  }
  return context;
};

export default PracticeContext;
